// src/app/components/admin/theme/theme.component.ts

import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import {
  LangChangeEvent,
  TranslateModule,
  TranslateService,
} from '@ngx-translate/core';
import { Subject as RxJsSubject } from 'rxjs';
import { takeUntil, finalize } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { matchesSearch } from '../../../../core/utils/search-text';
import { FormsModule } from '@angular/forms';

import { CustomTabComponent } from '../../../ui/custom-tab/custom-tab.component';
import {
  SearchableSelectComponent,
  SelectItem,
} from '../../../ui/searchable-select/searchable-select.component';
import { FilterBarComponent } from '../../../ui/filter-bar/filter-bar.component';
import { ButtonComponent } from '../../../ui/button/button.component';
import { SyncfusionL10nService } from '../../../../shared/utils/syncfusion-l10n.service';
import { LanguageService } from '../../../../core/services/utils/language.service';
import { SwalService } from '../../../../core/services/utils/swal.service';
import { ModalComponent } from '../../../ui/modal/modal.component';
import { AddThemeComponent } from '../../../../shared/modals/admin/add-theme/add-theme.component';
import { LearningService } from '../../../../core/services/learning-path.service';
import {
  Theme,
  CreateThemeDto,
  UpdateThemeDto,
} from '../../../../core/interfaces/theme';
import { Subject } from '../../../../core/interfaces/subject';
import { LearningPath } from '../../../../core/interfaces/learning-path';

/** Matière rattachée à son parcours, pour la cascade des facettes. */
interface SubjectOption extends Subject {
  learningPathTitle: string;
}

/** Thème enrichi de sa matière et de son parcours, pour colonnes et recherche. */
interface ThemeRow extends Theme {
  subjectName: string;
  learningPathId: number;
  learningPathTitle: string;
}

@Component({
  selector: 'app-admin-theme',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    CustomTabComponent,
    SearchableSelectComponent,
    FilterBarComponent,
    ButtonComponent,
    ModalComponent,
    AddThemeComponent,
    FormsModule,
  ],
  templateUrl: './theme.component.html',
  styleUrl: './theme.component.css',
})
export class ThemeComponent implements OnInit, OnDestroy {
  private translate = inject(TranslateService);
  private syncfusionL10n = inject(SyncfusionL10nService);
  private languageService = inject(LanguageService);
  private learningService = inject(LearningService);
  private swalService = inject(SwalService);
  private destroy$ = new RxJsSubject<void>();

  /** Tous les thèmes du référentiel ; le filtrage se fait côté client. */
  themes: ThemeRow[] = [];
  /** Toutes les matières, avec le parcours auquel elles appartiennent. */
  subjects: SubjectOption[] = [];
  learningPaths: LearningPath[] = [];

  /** Facettes. `null` = pas de filtre (« tous » / « toutes »). */
  selectedPathId: number | null = null;
  selectedSubjectId: number | null = null;
  search = '';

  /** Pas de recherche intégrée à la table : la barre de filtres porte la seule. */
  readonly tableToolbar: string[] = [];

  /** Listes dérivées matérialisées (voir la note dans l'écran professeurs). */
  pathOptions: SelectItem[] = [];
  subjectOptions: SelectItem[] = [];
  visibleThemes: ThemeRow[] = [];

  isRTL = false;
  currentLocale = 'ar';
  isLoading = false;
  isLoadingPaths = false;
  isLoadingSubjects = false;
  isSaving = false;
  error: string | null = null;

  translatedHeaders = {
    title: '',
    description: '',
    subject: '',
    learningPath: '',
    displayOrder: '',
    status: '',
    actions: '',
  };

  themeColumns: Array<{
    field: string;
    headerText: string;
    width: number;
    textAlign?: string;
    template?: string;
    freeze?: string;
    hideBelow?: 'md' | 'lg';
  }> = [];

  showThemeModal = false;
  selectedTheme: Theme | null = null;
  isEditMode = false;

  ngOnInit(): void {
    this.initializeComponent();
    this.subscribeToLanguageChanges();
    this.loadLearningPaths();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeComponent(): void {
    this.currentLocale = this.languageService.getCurrentLang();
    this.isRTL = !this.languageService.isLTR();
    this.loadTranslations();
  }

  private subscribeToLanguageChanges(): void {
    this.translate.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: LangChangeEvent) => {
        this.currentLocale = event.lang;
        this.isRTL = !this.languageService.isLTR();
        this.loadTranslations();
      });
  }

  loadTranslations(): void {
    this.translate
      .get([
        'DATA_TABLE.TITLE',
        'DATA_TABLE.DESC',
        'DATA_TABLE.SUBJECT',
        'DATA_TABLE.LEARNING_PATH',
        'DATA_TABLE.DISPLAY_ORDER',
        'DATA_TABLE.STATUS',
        'DATA_TABLE.ACTIONS',
      ])
      .pipe(takeUntil(this.destroy$))
      .subscribe((translations) => {
        this.translatedHeaders = {
          title: translations['DATA_TABLE.TITLE'],
          description: translations['DATA_TABLE.DESC'],
          subject: translations['DATA_TABLE.SUBJECT'],
          learningPath: translations['DATA_TABLE.LEARNING_PATH'],
          displayOrder: translations['DATA_TABLE.DISPLAY_ORDER'],
          status: translations['DATA_TABLE.STATUS'],
          actions: translations['DATA_TABLE.ACTIONS'],
        };
        this.buildColumns();
      });
  }

  buildColumns(): void {
    this.themeColumns = [
      {
        field: 'title',
        headerText: this.translatedHeaders.title,
        width: 200,
        textAlign: this.isRTL ? 'Right' : 'Left',
      },
      {
        field: 'description',
        headerText: this.translatedHeaders.description,
        width: 250,
        textAlign: this.isRTL ? 'Right' : 'Left',
      },
      // La liste peut désormais couvrir plusieurs matières / parcours : sans
      // ces deux colonnes, on ne saurait plus à quoi un thème se rattache.
      {
        field: 'subjectName',
        headerText: this.translatedHeaders.subject,
        width: 160,
        textAlign: this.isRTL ? 'Right' : 'Left',
      },
      {
        field: 'learningPathTitle',
        headerText: this.translatedHeaders.learningPath,
        width: 160,
        textAlign: this.isRTL ? 'Right' : 'Left',
        hideBelow: 'lg',
      },
      {
        field: 'displayOrder',
        headerText: this.translatedHeaders.displayOrder,
        width: 120,
        textAlign: 'Center',
      },
      {
        field: 'isActive',
        headerText: this.translatedHeaders.status,
        width: 100,
        textAlign: 'Center',
      },
      {
        field: 'id',
        headerText: '',
        width: 170,
        freeze: 'Right',
        textAlign: 'Center',
        template: 'actionTemplate',
      },
    ];
  }

  loadLearningPaths(): void {
    this.loadThemes();
  }

  /**
   * Charge parcours, matières **et** thèmes en un seul aller-retour
   * (`includeSubjects` + `includeThemes`) : c'est ce qui rend possible
   * « tous les thèmes d'un parcours » sans une requête par matière.
   */
  loadThemes(): void {
    this.isLoading = true;
    this.isLoadingPaths = true;
    this.isLoadingSubjects = true;
    this.error = null;

    this.learningService
      .getLearningPaths(true, true)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isLoading = false;
          this.isLoadingPaths = false;
          this.isLoadingSubjects = false;
        }),
      )
      .subscribe({
        next: (response) => {
          this.learningPaths = response.data ?? [];

          this.subjects = this.learningPaths.flatMap((path) =>
            (path.subjects ?? []).map((subject) => ({
              ...subject,
              learningPathId: subject.learningPathId ?? path.id,
              learningPathTitle: path.title,
            })),
          );

          this.themes = this.subjects.flatMap((subject) =>
            (subject.themes ?? []).map((theme) => ({
              ...theme,
              subjectId: theme.subjectId ?? subject.id,
              subjectName: subject.name,
              learningPathId: subject.learningPathId,
              learningPathTitle: subject.learningPathTitle,
            })),
          );

          this.rebuildPathOptions();
          this.rebuildSubjectOptions();
          this.applyFilters();
        },
        error: (error) => {
          console.error('Error loading themes:', error);
          this.translate
            .get('ADMIN.ERROR_LOADING_THEMES')
            .pipe(takeUntil(this.destroy$))
            .subscribe((message) => {
              this.error = message;
              this.swalService.warning(message);
            });
        },
      });
  }

  /* ── Facettes ────────────────────────────────────────────── */

  private rebuildPathOptions(): void {
    this.pathOptions = this.learningPaths.map((p) => ({
      id: p.id,
      label: p.title,
    }));
  }

  /** Matières proposées : celles du parcours choisi, sinon toutes. */
  private rebuildSubjectOptions(): void {
    const pathId = this.selectedPathId;
    const scoped =
      pathId == null
        ? this.subjects
        : this.subjects.filter((s) => s.learningPathId === pathId);
    this.subjectOptions = scoped.map((s) => ({ id: s.id, label: s.name }));
  }

  /** Nombre de facettes actives — pilote le compteur et la remise à zéro. */
  get activeFilterCount(): number {
    let n = 0;
    if (this.selectedPathId != null) n++;
    if (this.selectedSubjectId != null) n++;
    if (this.search.trim()) n++;
    return n;
  }

  private applyFilters(): void {
    const pathId = this.selectedPathId;
    const subjectId = this.selectedSubjectId;

    this.visibleThemes = this.themes.filter((t) => {
      // Une matière est plus précise que son parcours : dès qu'elle est
      // choisie, elle seule décide.
      if (subjectId != null) {
        if (t.subjectId !== subjectId) return false;
      } else if (pathId != null && t.learningPathId !== pathId) {
        return false;
      }
      return matchesSearch(this.search, t.title, t.description, t.subjectName);
    });
  }

  onPathChange(pathId: number | null): void {
    this.selectedPathId = pathId;
    this.rebuildSubjectOptions();
    // La matière choisie n'appartient probablement plus au nouveau parcours.
    if (
      this.selectedSubjectId != null &&
      !this.subjectOptions.some((o) => o.id === this.selectedSubjectId)
    ) {
      this.selectedSubjectId = null;
    }
    this.applyFilters();
  }

  onSubjectChange(subjectId: number | null): void {
    this.selectedSubjectId = subjectId;
    this.applyFilters();
  }

  onSearchChange(text: string): void {
    this.search = text;
    this.applyFilters();
  }

  resetFilters(): void {
    this.selectedPathId = null;
    this.selectedSubjectId = null;
    this.search = '';
    this.rebuildSubjectOptions();
    this.applyFilters();
  }

  handleRowClick(theme: Theme): void {
    console.log('Clicked theme:', theme);
  }

  addTheme(): void {
    // Aucun pré-requis de filtre : la matière se choisit dans le modal, qui
    // se contente de proposer celle du filtre courant par défaut.
    this.selectedTheme = null;
    this.isEditMode = false;
    this.showThemeModal = true;
  }

  onEditTheme(theme: Theme): void {
    this.selectedTheme = theme;
    this.isEditMode = true;
    this.showThemeModal = true;
  }

  onDeleteTheme(theme: Theme): void {
    this.translate
      .get('ADMIN.CONFIRM_DELETE_THEME')
      .pipe(takeUntil(this.destroy$))
      .subscribe((message) => {
        const confirmMessage = message.replace('{title}', theme.title);
        this.swalService.confirm(confirmMessage, () => {
          this.deleteTheme(theme.id);
        });
      });
  }

  private deleteTheme(themeId: number): void {
    this.learningService
      .deleteTheme(themeId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.translate
            .get('ADMIN.THEME_DELETED_SUCCESS')
            .pipe(takeUntil(this.destroy$))
            .subscribe((message) => {
              this.swalService.success(message);
              this.loadThemes();
            });
        },
        error: (error) => {
          console.error('Error deleting theme:', error);
          this.translate
            .get('ADMIN.ERROR_DELETING_THEME')
            .pipe(takeUntil(this.destroy$))
            .subscribe((message) => {
              this.swalService.warning(message);
            });
        },
      });
  }

  /**
   * Fiche du module, en lecture seule. Même défaut que la matière : le bouton
   * n'écrivait que dans la console.
   */
  onShowTheme(theme: Theme): void {
    const status = this.translate.instant(
      theme.isActive ? 'ADMIN.ACTIVE' : 'ADMIN.INACTIVE',
    );

    this.swalService.info(
      `<div style="text-align: start;">
        <strong>${this.translate.instant('ADMIN.SELECT_SUBJECT')}:</strong> ${this.esc(theme.subject?.name)}<br>
        <strong>${this.translate.instant('ADMIN.DESCRIPTION')}:</strong> ${this.esc(theme.description)}<br>
        <strong>${this.translate.instant('ADMIN.STATUS')}:</strong> ${this.esc(status)}
      </div>`,
      theme.title,
    );
  }

  // Replace the handleThemeSaved method in theme.component.ts with this enhanced version:

  handleThemeSaved(themeData: CreateThemeDto | UpdateThemeDto): void {
    // La matière vient du DTO (choisie dans le modal), plus du filtre : on ne
    // bloque donc plus l'enregistrement quand aucune matière n'est filtrée.
    this.isSaving = true;

    // Check if themeData has an id property to determine if it's an update
    const isUpdate = 'id' in themeData && themeData.id !== undefined;

    const operation = isUpdate
      ? this.learningService.updateTheme(
          (themeData as UpdateThemeDto).id,
          themeData as UpdateThemeDto,
        )
      : this.learningService.createTheme(themeData as CreateThemeDto);

    operation
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isSaving = false)),
      )
      .subscribe({
        next: (response) => {
          console.log('Success response:', response);
          const messageKey = isUpdate
            ? 'ADMIN.THEME_UPDATED_SUCCESS'
            : 'ADMIN.THEME_CREATED_SUCCESS';

          this.translate
            .get(messageKey)
            .pipe(takeUntil(this.destroy$))
            .subscribe((message) => {
              this.swalService.success(message);
              this.closeModal();
              this.loadThemes();
            });
        },
        error: (error) => {
          console.error('=== Error Details ===');
          console.error('Full error object:', error);
          console.error('Error status:', error.status);
          console.error('Error message:', error.message);
          console.error('Error details:', error.error);

          const errorKey = isUpdate
            ? 'ADMIN.ERROR_UPDATING_THEME'
            : 'ADMIN.ERROR_CREATING_THEME';

          this.translate
            .get(errorKey)
            .pipe(takeUntil(this.destroy$))
            .subscribe((message) => {
              this.swalService.warning(message);
            });
        },
      });
  }

  onModalClosed(): void {
    this.closeModal();
  }

  private closeModal(): void {
    this.showThemeModal = false;
    this.selectedTheme = null;
    this.isEditMode = false;
  }

  setGridLanguage(lang: string): void {
    this.currentLocale = lang;
  }

  /**
   * Échappe une valeur avant de la poser dans la fiche.
   *
   * <p>La fiche est du HTML rendu tel quel. Les libellés viennent de
   * l'administration : un chevron dans un intitulé casserait la mise en
   * forme, et une balise y exécuterait ce qu'elle porte.</p>
   */
  private esc(value: unknown): string {
    return String(value ?? '—')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
}
